import React, { useMemo, useState, useEffect } from "react";
import { useParams, useHistory, useLocation } from 'react-router-dom';
import {
  Search,
  BarChart3,
  Star,
  Paperclip,
  Plus,
  Bot,
  Zap,
  Calendar,
  CalendarCheck,
  MoreVertical,
  Trash2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import InvokeAgentModal from "./InvokeAgentModal";
import GA4Agent from "./GA4Agent"; // Your existing GA4Agent component
import Schedule from "./Schedule"; // Schedule component
import Sidebar from "../common/sidebar";
import TopBar from "../common/topbar";
import { OrganizationProvider, useOrganization } from '../../../context/OrganizationContext';
import { useUser } from '../../../context/UserContext';
import { getThreads, deleteThread } from '../../../services/threadService';
import { getGA4Status, initiateGA4OAuth } from '../../../services/externalService';


// ----------------------------------------------------------------------------
// Component helpers
// ----------------------------------------------------------------------------

// Helper function to check if a thread has a threadId (agent has been run)
const hasThreadId = (thread) => {
  try {
    return thread?.thread_id !== null && thread?.thread_id !== undefined;
  } catch {
    return false;
  }
};

function ThreadRow({ thread, selected, onSelect, onSchedule, onDelete, isScheduleActive }) {
  const [showMenu, setShowMenu] = React.useState(false);
  
  const handleScheduleClick = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    if (onSchedule) {
      onSchedule(thread);
    }
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    if (onDelete) {
      onDelete(thread);
    }
  };

  const handleMenuToggle = (e) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  // Check if automation is eligible for this thread
  const isAutomationEligible = thread?.config?.automation?.eligible === true;

  return (
    <button
      onClick={onSelect}
      onMouseLeave={() => setShowMenu(false)}
      className={`group relative grid w-full grid-cols-[20px_1fr_auto_auto] gap-3 px-4 py-3 text-left transition-all duration-200 ${
        selected 
          ? "bg-brand-blue-light/30" 
          : "hover:bg-brand-blue-light/10"
      }`}
    >
      <div className="flex items-center justify-center">
        {thread.starred ? (
          <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
        ) : hasThreadId(thread) ? (
          <Zap className="h-4 w-4 text-brand-pink" />
        ) : (
          <Bot className="h-4 w-4 text-grey" />
        )}
      </div>
      
      <div className="min-w-0">
        <div className="flex items-center gap-2 truncate">
          <span className={`truncate text-sm font-medium ${selected ? 'text-brand-blue-dark' : 'text-foreground'}`}>
            {thread.prompt || "GA4 Analysis"}
          </span>
        </div>
        <div className="flex items-center gap-2 text-xs text-grey">
          <span className="truncate">
            {thread.agentType} • {thread.periodType} • {new Date(thread.started_at).toLocaleDateString()}
          </span>
          {thread.scheduled && (
            <span
              className="shrink-0 inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700"
              title="This thread has scheduled automation"
            >
              <CalendarCheck className="h-3 w-3" />
              Scheduled
            </span>
          )}
        </div>
      </div>
      
      {/* Time - always visible */}
      <div className="flex items-center">
        <span className="text-xs text-grey whitespace-nowrap">
          {new Date(thread.started_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
      
      {/* Actions area - fixed width */}
      <div className="flex items-center justify-end gap-1 w-16 relative">
        {/* Schedule button - fixed position, only for automation-eligible threads */}
        {isAutomationEligible && !thread.scheduled && (
          <button
            onClick={handleScheduleClick}
            className="p-1.5 rounded-md hover:scale-110 transition-all duration-200 group/btn relative"
            title="Schedule automation"
          >
            {isScheduleActive ? (
              <CalendarCheck className="h-4 w-4 text-brand-pink" />
            ) : (
              <>
                <Calendar className="h-4 w-4 text-brand-blue-dark group-hover/btn:opacity-0 transition-opacity absolute" />
                <CalendarCheck className="h-4 w-4 text-brand-pink opacity-0 group-hover/btn:opacity-100 transition-opacity" />
              </>
            )}
          </button>
        )}
        
        {/* More menu button - appears on hover or when selected */}
        <div className={`${selected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}>
          <button
            onClick={handleMenuToggle}
            className="p-1 rounded hover:bg-gray-200 transition-colors"
            title="More actions"
          >
            <MoreVertical className="h-4 w-4 text-grey" />
          </button>
        </div>
        
        {/* Dropdown menu */}
        {showMenu && (
          <div 
            className="absolute right-0 top-full mt-1 bg-white border border-border rounded-lg shadow-lg py-1 z-50 min-w-[140px]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleDeleteClick}
              className="w-full px-3 py-2 text-left text-sm hover:bg-red-50 flex items-center gap-2 text-red-600"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete
            </button>
          </div>
        )}
      </div>
    </button>
  );
}

// ----------------------------------------------------------------------------
// Schedule Component Placeholder
// ----------------------------------------------------------------------------
// Removed - now using dedicated Schedule.jsx component

// ----------------------------------------------------------------------------
// Main component
// ----------------------------------------------------------------------------
const GA4ThreadsAppContent = () => {
  const { organizationId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const { organizationData, fetchOrganizationData } = useOrganization();
  const { userInfo } = useUser();
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState(null); // MongoDB _id from server
  const [activeView, setActiveView] = useState("agent"); // "agent" | "schedule" | "other-actions"
  const [threads1, setThreads1] = useState([]); // Backend threads state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [shouldRunAgent, setShouldRunAgent] = useState(null); // Store thread to auto-run
  
  // GA4 connection state
  const [isGA4Connected, setIsGA4Connected] = useState(null); // null = loading, true/false
  const [ga4Loading, setGA4Loading] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalThreads, setTotalThreads] = useState(0);
  const [pageSize, setPageSize] = useState(50); // Actual page size from server
  const [isLoadingThreads, setIsLoadingThreads] = useState(false);
  const THREADS_PER_PAGE = 20; // Requested page size

  // Get threadId from URL query params
  const getThreadIdFromUrl = () => {
    const params = new URLSearchParams(location.search);
    return params.get('threadId');
  };

  // Update URL with threadId without page reload
  const updateUrlWithThreadId = (threadId) => {
    const params = new URLSearchParams(location.search);
    if (threadId) {
      params.set('threadId', threadId);
    } else {
      params.delete('threadId');
    }
    const newSearch = params.toString();
    const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
    history.replace(newUrl);
  };

  useEffect(() => {
    if (organizationId) {
      fetchOrganizationData(organizationId);
    }
  }, [organizationId, fetchOrganizationData]);

  // Check GA4 connection status
  useEffect(() => {
    const checkGA4Status = async () => {
      if (organizationId) {
        try {
          const response = await getGA4Status(organizationId);
          setIsGA4Connected(response.data?.connected || false);
        } catch (error) {
          setIsGA4Connected(false);
        }
      }
    };
    checkGA4Status();
  }, [organizationId]);

  const handleConnectGA4 = async () => {
    setGA4Loading(true);
    try {
      const response = await initiateGA4OAuth(organizationId);
      window.location.href = response.data.authUrl;
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to initiate GA4 connection');
      setGA4Loading(false);
    }
  };

  // Fetch threads from backend
  useEffect(() => {
    const fetchThreads = async () => {
      if (organizationId) {
        setIsLoadingThreads(true);
        try {
          const response = await getThreads(organizationId, {
            page: currentPage,
            limit: THREADS_PER_PAGE
          });
          
          // Handle both paginated and non-paginated server responses
          // Server returns: { data: [...], pagination: { currentPage, pageSize, totalItems, totalPages } }
          const responseData = response?.data;
          const threadsArray = responseData?.data || [];
          const pagination = responseData?.pagination;
          
          setThreads1(threadsArray);
          
          // If server returns pagination info, use it; otherwise calculate from data
          if (pagination) {
            // Support both naming conventions: totalPages/totalItems (server) or total/page (alternative)
            setTotalPages(pagination.totalPages || pagination.pages || 1);
            setTotalThreads(pagination.totalItems || pagination.total || threadsArray.length);
            setPageSize(pagination.pageSize || pagination.limit || THREADS_PER_PAGE);
          } else {
            // Fallback for non-paginated server: assume all data returned
            setTotalPages(1);
            setTotalThreads(threadsArray.length);
            setPageSize(THREADS_PER_PAGE);
          }
          
          // Check if there's a threadId in URL query params
          const urlThreadId = getThreadIdFromUrl();
          
          if (urlThreadId && threadsArray.some(t => t._id === urlThreadId)) {
            // Select the thread from URL if it exists
            setSelectedId(urlThreadId);
          } else if (threadsArray.length > 0 && !selectedId) {
            // Only auto-select first thread if nothing is selected
            setSelectedId(threadsArray[0]._id);
            updateUrlWithThreadId(threadsArray[0]._id);
          }
        } catch (error) {
          setThreads1([]);
        } finally {
          setIsLoadingThreads(false);
        }
      }
    };
    fetchThreads();
  }, [organizationId, currentPage]);

  const handleAddThread = (newThread) => {
    setThreads1(prev => [newThread, ...prev]);
    // Use _id from server response as the primary identifier
    setSelectedId(newThread._id);
    updateUrlWithThreadId(newThread._id);
    setActiveView("agent"); // Reset to agent view when adding new thread
  };

  const handleRunAgent = (newThread) => {
    setShouldRunAgent(newThread);
  };

  // Handle schedule action
  const handleSchedule = (thread) => {
    setSelectedId(thread._id);
    updateUrlWithThreadId(thread._id);
    setActiveView("schedule");
  };

  // Handle delete action
  const handleDelete = async (thread) => {
    const threadName = thread.prompt || 'this thread';
    
    if (window.confirm(`Are you sure you want to delete "${threadName}"?\n\nThis will also delete any scheduled reports associated with this thread.`)) {
      try {
        // Call backend service to delete the thread
        await deleteThread(organizationId, thread._id);
        
        // Remove from local state - match by _id
        setThreads1(prev => prev.filter(t => t._id !== thread._id));
        
        // If deleted thread was selected, clear selection and URL param
        if (selectedId === thread._id) {
          setSelectedId(null);
          updateUrlWithThreadId(null);
          setActiveView("agent");
        }
      } catch (error) {
        alert(`Failed to delete thread: ${error.response?.data?.message || error.message}`);
      }
    }
  };

  // Handle thread selection (clicking row)
  const handleThreadSelect = (threadId) => {
    setSelectedId(threadId);
    updateUrlWithThreadId(threadId);
    setActiveView("agent"); // Always show agent view when clicking row
  };

  // Pagination handlers
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Filter threads by search query
  const filtered = useMemo(() => {
    if (!query) return threads1;
    const q = query.toLowerCase();
    return threads1.filter(
      (thread) =>
        thread.prompt?.toLowerCase().includes(q) ||
        thread.agentType?.toLowerCase().includes(q) ||
        thread.periodType?.toLowerCase().includes(q)
    );
  }, [query, threads1]);

  const selected = useMemo(() => {
    return threads1.find((m) => {
      // Match by MongoDB _id (primary identifier from server)
      return m._id === selectedId;
    });
  }, [selectedId, threads1]);

  if (!organizationData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#CC457D] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading organization...</p>
        </div>
      </div>
    );
  }

  // Show thread management interface with sidebar and topbar
  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar />
      <div className="flex flex-col flex-1 overflow-hidden">
        <TopBar />
        
        {/* Main content area */}
        <div className="flex-1 overflow-hidden">
          <div className="flex h-full">
            {/* Thread list panel with header */}
            <div className="flex flex-col w-[440px] bg-background border-r border-border">
              {/* Top bar - search and create thread - aligned with sidebar navigation start */}
              <header className="flex items-center gap-2 border-b border-border px-4 h-[62px] bg-background/50">
                <div className="flex flex-1 items-center gap-1.5 rounded border border-border px-2 py-0.5 bg-background focus-within:border-primary focus-within:bg-background transition-all duration-200 h-6">
                  <Search className="h-3 w-3 text-grey flex-shrink-0" />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search..."
                    className="w-full bg-transparent text-xs outline-none placeholder:text-grey border-none p-0 m-0 min-w-0 focus:outline-none focus:ring-0 focus:border-none"
                  />
                </div>
                <button 
                  onClick={() => setIsModalOpen(true)}
                  className="flex items-center gap-1 rounded px-2 py-0.5 bg-brand-pink hover:bg-brand-pink/90 text-white text-xs font-medium transition-all duration-200 border border-transparent h-6"
                >
                  <Plus className="h-3 w-3" />
                  <span>New </span>
                </button>
              </header>

              {/* Thread list */}
              <section className="flex min-h-0 flex-col flex-1 bg-background">
                <div className="min-h-0 flex-1 overflow-y-auto">
                  {isLoadingThreads ? (
                    <div className="p-8 text-center text-grey">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-pink mx-auto mb-4"></div>
                      <p className="text-sm">Loading threads...</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-border">
                      {filtered.length === 0 ? (
                        <div className="p-8 text-center text-grey">
                          <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-brand-blue-light/20 flex items-center justify-center">
                            <BarChart3 className="h-8 w-8 text-brand-blue-dark opacity-50" />
                          </div>
                          <p className="text-sm mb-2 font-medium">No threads available</p>
                          <p className="text-xs opacity-75 mb-4">Create a new thread to get started</p>
                          <button
                            onClick={() => setIsModalOpen(true)}
                            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-brand-pink hover:bg-brand-pink/90 text-white text-xs font-medium rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
                          >
                            <Plus className="h-3.5 w-3.5" />
                            New Thread
                          </button>
                        </div>
                      ) : (
                        filtered.map((m) => {
                          // Use MongoDB _id as the primary identifier
                          const isScheduleActive = selectedId === m._id && activeView === "schedule";
                          return (
                            <ThreadRow
                              key={m._id}
                              thread={m}
                              selected={selectedId === m._id}
                              onSelect={() => handleThreadSelect(m._id)}
                              onSchedule={handleSchedule}
                              onDelete={handleDelete}
                              isScheduleActive={isScheduleActive}
                            />
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
                
                {/* Pagination footer */}
                <div className="flex items-center justify-between border-t border-border px-4 py-2 bg-background/50">
                  <div className="text-xs text-grey">
                    {totalThreads > 0 ? (
                      <span>
                        {totalPages > 1 
                          ? `${((currentPage - 1) * pageSize) + 1}-${Math.min(currentPage * pageSize, totalThreads)} of ${totalThreads}`
                          : `${totalThreads} thread${totalThreads !== 1 ? 's' : ''}`
                        }
                      </span>
                    ) : (
                      <span>No threads</span>
                    )}
                  </div>
                  {totalPages > 1 && (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1 || isLoadingThreads}
                        className="p-1 rounded hover:bg-brand-blue-light/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        title="Previous page"
                      >
                        <ChevronLeft className="h-4 w-4 text-grey" />
                      </button>
                      <div className="flex items-center gap-0.5">
                        {/* Show page numbers */}
                        {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              disabled={isLoadingThreads}
                              className={`min-w-[24px] h-6 text-xs rounded transition-colors ${
                                currentPage === pageNum
                                  ? 'bg-brand-pink text-white font-medium'
                                  : 'hover:bg-brand-blue-light/20 text-grey'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>
                      <button
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages || isLoadingThreads}
                        className="p-1 rounded hover:bg-brand-blue-light/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        title="Next page"
                      >
                        <ChevronRight className="h-4 w-4 text-grey" />
                      </button>
                    </div>
                  )}
                </div>
              </section>
            </div>

            {/* Right: Reading pane - Show different views based on activeView state */}
            <section className="hidden flex-1 flex-col md:flex bg-background">
              {selected ? (
                <div className="flex-1 overflow-hidden border-l border-border" key={selected._id}>
                  {/* Render different components based on activeView */}
                  {activeView === "agent" && (
                    <GA4Agent 
                      thread={selected} 
                      threads1={threads1} 
                      setThreads1={setThreads1}
                      shouldRunAgent={shouldRunAgent}
                      onAgentRun={() => setShouldRunAgent(null)}
                    />
                  )}
                  {activeView === "schedule" && (
                    <Schedule 
                      thread={selected}
                      threads1={threads1}
                      setThreads1={setThreads1}
                      onBack={() => setActiveView("agent")}
                    />
                  )}
                  {/* Add more views here as needed:
                  {activeView === "duplicate" && <DuplicateView thread={selected} />}
                  {activeView === "share" && <ShareView thread={selected} />}
                  */}
                </div>
              ) : (
                <div className="border-l border-border bg-gradient-to-br from-brand-blue-light/20 via-brand-blue-light/10 to-brand-blue-light/5 min-h-0 flex-1 overflow-y-auto flex items-center justify-center">
                  <div className="text-center p-8 max-w-md">
                    <div className="mx-auto mb-6 w-20 h-20 rounded-full bg-brand-blue-light flex items-center justify-center">
                      <BarChart3 className="h-10 w-10 text-brand-blue-dark" />
                    </div>
                    {isGA4Connected === false ? (
                      <>
                        <h3 className="text-xl font-semibold text-foreground mb-3">
                          Connect Your GA4 Property
                        </h3>
                        <p className="text-grey mb-8 leading-relaxed">
                          Connect your Google Analytics 4 property to start analyzing your data with AI-powered insights.
                        </p>
                        <div className="flex flex-col gap-3 items-center">
                          <button
                            onClick={handleConnectGA4}
                            disabled={ga4Loading}
                            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-brand-pink hover:bg-brand-pink/90 text-white rounded-lg font-medium transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            {ga4Loading ? (
                              <>
                                <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                Connecting...
                              </>
                            ) : (
                              <>
                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                </svg>
                                Connect Google Analytics
                              </>
                            )}
                          </button>
                          <a 
                            href={`/organization/${organizationId}/settings`} 
                            className="text-xs text-grey hover:text-foreground transition-colors"
                          >
                            Go to Settings
                          </a>
                        </div>
                      </>
                    ) : (
                      <>
                        <h3 className="text-xl font-semibold text-foreground mb-3">
                          Select a Thread
                        </h3>
                        <p className="text-grey mb-8 leading-relaxed">
                          Choose a thread from the list to start your GA4 analysis or create a new one to get started.
                        </p>
                      </>
                    )}
                  </div>
                </div>
              )}
            </section>    
          </div>
        </div>

        {/* Add Thread Modal */}
        <InvokeAgentModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onAdd={handleAddThread}
          onRunAgent={handleRunAgent}
          isGA4Connected={isGA4Connected}
        />
      </div>
    </div>
  );
};

// Main component that provides the organization context
const GA4ThreadsApp = () => {
  return (
    <OrganizationProvider>
      <GA4ThreadsAppContent />
    </OrganizationProvider>
  );
};

export default GA4ThreadsApp;